import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import {
    Box,
    Typography,
    Grid,
    TextField,
    Button,
    IconButton,
    Paper,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getAllSeasonsAction,
    getAllZonesAction,
    getAllRegionsAction,
    getAllAreasAction,
    getAllDistrictsAction,
    getAllMandalsAction,
    getAllCropsAction,
    getAllVarietiesAction,
    createSalesTargetAction, clearCreateSalesTargetAction, updateSalesTargetAction,
    getSalesTargetByIdAction, clearGetSalesTargetByIdAction, clearUpdateSalesTargetAction
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

const CreateSalesTarget = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [formPopulated, setFormPopulated] = useState(false);

    const { successData: seasons } = useSelector(
        (state) => state.getAllSeasonsReducer
    );
    const seasonList = seasons?.data?.seasons || [];

    const { successData: zones } = useSelector((state) => state.getAllZonesReducer);
    const zoneList = zones?.data?.zones || [];

    const { successData: regions } = useSelector((state) => state.getAllRegionsReducer);
    const regionList = regions?.data?.regions || [];

    const { successData: areas } = useSelector((state) => state.getAllAreasReducer);
    const areaList = areas?.data?.areas || [];

    const { successData: districts } = useSelector(state => state.getAllDistrictsReducer);
    const districtList = districts?.data?.districts || [];

    const { successData: mandals } = useSelector(state => state.getAllMandalsReducer || {});
    const mandalList = mandals?.data?.mandals || [];

    const { successData: crops } = useSelector(state => state.getAllCropsReducer);
    const cropList = crops?.data?.crops || [];

    const { successData: varieties } = useSelector(state => state.getAllVarietiesReducer);
    const varietyList = varieties?.data?.varieties || [];

    const {successData: createSuccess, error: createError, loading: createLoading, errorInfo} = useSelector((state) => state.createSalesTargetReducer || {});
    const { successData: updateSuccess, error: updateError, loading: updateLoading, errorInfo: updateErrorInfo } = useSelector((state) => state.updateSalesTargetReducer || {});
    const { successData: salesTargetById } = useSelector((state) => state.getSalesTargetByIdReducer || {});

    const editId = location.state?.id;


    useEffect(() => {
        dispatch(getAllSeasonsAction());
        dispatch(getAllZonesAction());
        dispatch(getAllRegionsAction());
        dispatch(getAllAreasAction());
        dispatch(getAllDistrictsAction());
        dispatch(getAllMandalsAction());
        dispatch(getAllCropsAction());
        dispatch(getAllVarietiesAction());
    }, [dispatch]);

    useEffect(() => {
        if (editId) {
            dispatch(clearGetSalesTargetByIdAction());
            setFormPopulated(false);
            dispatch(getSalesTargetByIdAction({ endPoint: `target/sales/${editId}` }));
        }
    }, [dispatch, editId]);


    useEffect(() => {
        if (!editId && createSuccess) {
            toast.success(createSuccess?.message || "Sales target created successfully");
            dispatch(clearCreateSalesTargetAction());
            navigate(-1);
        }
    }, [createSuccess, dispatch, navigate, editId]);

    useEffect(() => {
        if (!editId && createError) {
            toast.error(errorInfo?.message || "Failed to create sales target");
            dispatch(clearCreateSalesTargetAction());
        }
    }, [createError, dispatch, errorInfo, editId]);

    useEffect(() => {
        if (editId && updateSuccess) {
            toast.success(updateSuccess?.message || "Sales target updated successfully");
            dispatch(clearUpdateSalesTargetAction());
            navigate(-1);
        }
        if (editId && updateError) {
            toast.error(updateErrorInfo || "Failed to update sales target");
            dispatch(clearUpdateSalesTargetAction());
        }
    }, [editId, updateSuccess, updateError, updateErrorInfo, dispatch, navigate]);

    /* -------------------- FORMIK -------------------- */
    const formik = useFormik({
        initialValues: {
            seasonId: "",
            targetLocationType: "",
            zone: "",
            region: "",
            area: "",
            district: "",
            mandal: "",
            targets: [],
            newTarget: {
                crop: "",
                variety: "",
                pySalesQty: "",
                salesTargetQty: "",
            },
        },
        onSubmit: (values) => {
            // Filter out incomplete targets
            const validTargets = (values.targets || []).filter(t =>
                t.crop && t.variety && t.pySalesQty !== '' && t.salesTargetQty !== ''
            );
            if (!validTargets.length) {
                toast.error("Please add at least one valid target detail.");
                return;
            }

            const toDecimalString = (val) => {
                const num = Number(val);
                return isNaN(num) ? "0.00" : num.toFixed(2);
            };

            const pyTotal = validTargets.reduce(
                (sum, t) => sum + Number(t.pySalesQty || 0),
                0
            );
            const targetTotal = validTargets.reduce(
                (sum, t) => sum + Number(t.salesTargetQty || 0),
                0
            );

            const targetType = (values.targetLocationType || "").toLowerCase();
            const targetAreaIdMap = {
                zone: values.zone,
                region: values.region,
                area: values.area,
                district: values.district,
                mandal: values.mandal,
            };

            let target_area_id = targetAreaIdMap[targetType];
            if (!target_area_id || isNaN(Number(target_area_id))) {
                toast.error("Please select a valid target area.");
                return;
            }
            target_area_id = Number(target_area_id);

            // If editing, include detail id if present
            const details = validTargets.map((t) => {
                const detail = {
                    crop_id: Number(t.crop),
                    variety_id: Number(t.variety),
                    py_sales_qty: toDecimalString(t.pySalesQty),
                    sales_target: toDecimalString(t.salesTargetQty),
                };
                if (editId && t.id) {
                    detail.id = t.id;
                }
                return detail;
            });

            const payload = {
                season_id: values.seasonId ? Number(values.seasonId) : undefined,
                target_area_type: targetType,
                target_area_id,
                py_total_sales_qty: toDecimalString(pyTotal),
                total_sales_target_qty: toDecimalString(targetTotal),
                details,
            };

            if (editId) {
                dispatch(updateSalesTargetAction({ endPoint: `target/sales/${editId}`, ...payload }));
            } else {
                dispatch(createSalesTargetAction(payload));
            }
        },
    });

    /* -------------------- LOCATION HANDLING -------------------- */
    const fieldsByType = useMemo(() => ({
        Zone: ["zone"],
        Region: ["zone", "region"],
        Area: ["zone", "region", "area"],
        district: ["zone", "region", "area", "district"],
        Mandal: ["zone", "region", "area", "district", "mandal"],
    }), []);

    const visibleFields = useMemo(() => (
        fieldsByType[formik.values.targetLocationType] || []
    ), [fieldsByType, formik.values.targetLocationType]);

    useEffect(() => {
        ["zone", "region", "area", "district", "mandal"].forEach((f) => {
            if (!visibleFields.includes(f)) {
                formik.setFieldValue(f, "");
            }
        });
      }, [visibleFields]);

    const filteredRegions = regionList.filter(
        (r) => r.zoneId === formik.values.zone
    );
    const filteredAreas = areaList.filter(
        (a) => a.regionId === formik.values.region
    );
    const filteredDistricts = districtList.filter(
        (d) => d.areaId === formik.values.area
    );

    const filteredMandals = mandalList.filter(
        (m) => m.districtId === formik.values.district
    );

    useEffect(() => {
        if (editId && salesTargetById && salesTargetById.data && !formPopulated) {
            const t = salesTargetById.data;
            const typeLower = (t.target_area_type || '').toLowerCase();
            const typeMap = {
                zone: 'Zone',
                region: 'Region',
                area: 'Area',
                district: 'district',
                mandal: 'Mandal',
            };
            const targetTypeUi = typeMap[typeLower] || '';

            let zoneIdVal = '';
            let regionIdVal = '';
            let areaIdVal = '';
            let districtIdVal = '';
            let mandalIdVal = '';
            const id = t.target_area_id;

            if (typeLower === 'zone') {
                zoneIdVal = id;
            } else if (typeLower === 'region') {
                regionIdVal = id;
                const reg = regionList.find(r => r.regionId === id);
                zoneIdVal = reg?.zoneId || '';
            } else if (typeLower === 'area') {
                areaIdVal = id;
                const area = areaList.find(a => a.areaId === id);
                const reg = regionList.find(r => r.regionId === area?.regionId);
                regionIdVal = area?.regionId || '';
                zoneIdVal = reg?.zoneId || '';
            } else if (typeLower === 'district') {
                districtIdVal = id;
                const dist = districtList.find(d => d.districtId === id);
                const area = areaList.find(a => a.areaId === dist?.areaId);
                const reg = regionList.find(r => r.regionId === area?.regionId);
                areaIdVal = dist?.areaId || '';
                regionIdVal = area?.regionId || '';
                zoneIdVal = reg?.zoneId || '';
            } else if (typeLower === 'mandal') {
                mandalIdVal = id;
                const man = mandalList.find(m => m.mandalId === id);
                const dist = districtList.find(d => d.districtId === man?.districtId);
                const area = areaList.find(a => a.areaId === dist?.areaId);
                const reg = regionList.find(r => r.regionId === area?.regionId);
                districtIdVal = man?.districtId || '';
                areaIdVal = dist?.areaId || '';
                regionIdVal = area?.regionId || '';
                zoneIdVal = reg?.zoneId || '';
            }

            // Always include id in targets for edit
            const details = Array.isArray(t.salesTargetDetails) ? t.salesTargetDetails : [];
            const targets = details.map(d => ({
                id: d.id, // always include id for update
                crop: d.crop_id,
                variety: d.variety_id,
                pySalesQty: d.py_sales_qty,
                salesTargetQty: d.sales_target,
            }));

            formik.setValues({
                seasonId: t.season_id || t.season?.season_id || '',
                targetLocationType: targetTypeUi,
                zone: zoneIdVal || '',
                region: regionIdVal || '',
                area: areaIdVal || '',
                district: districtIdVal || '',
                mandal: mandalIdVal || '',
                targets: targets,
                newTarget: { crop: '', variety: '', pySalesQty: '', salesTargetQty: '' },
            });
            setFormPopulated(true);
        }
    }, [editId, salesTargetById, formPopulated, regionList, areaList, districtList, mandalList]);

    /* -------------------- TARGET ROWS -------------------- */
    const handleAddTarget = () => {
        formik.setFieldValue("targets", [
            ...formik.values.targets,
            formik.values.newTarget,
        ]);
        formik.setFieldValue("newTarget", {
            crop: "",
            variety: "",
            pySalesQty: "",
            salesTargetQty: "",
        });
    };

    const handleDeleteTarget = (idx) => {
        formik.setFieldValue(
            "targets",
            formik.values.targets.filter((_, i) => i !== idx)
        );
    };

    const pyTotal = formik.values.targets.reduce(
        (s, t) => s + Number(t.pySalesQty || 0),
        0
    );
    const targetTotal = formik.values.targets.reduce(
        (s, t) => s + Number(t.salesTargetQty || 0),
        0
    );

    const canAdd =
        formik.values.newTarget.crop &&
        formik.values.newTarget.variety &&
        formik.values.newTarget.pySalesQty !== "" &&
        formik.values.newTarget.salesTargetQty !== "";

    const filteredVarieties = varietyList.filter(
        (v) => v.cropId === formik.values.newTarget.crop
    );

    /* -------------------- UI -------------------- */
    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3, md: 4 },
                mt: {  sm: 8, md: 0 },
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
                // m: 2,
                position: "relative"
            }}
        >
            <IconButton
                aria-label="close"
                onClick={() => navigate(-1)}
                sx={{ position: "absolute", top: 8, right: 8 }}
            >
                <CloseIcon />
            </IconButton>
            <Box component="form" onSubmit={formik.handleSubmit}>
                <Typography variant="h6" fontWeight={600}>
                    Sale Target Information
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>
                    Create and manage sales target
                </Typography>

                <Typography fontWeight={600} mb={1}>
                    Basic Information
                </Typography>
                {/* Season & Location Type */}
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Autocomplete
                            options={seasonList}
                            value={
                                seasonList.find((s) => s.seasonId === formik.values.seasonId) || null
                            }
                            onChange={(e, val) =>
                                formik.setFieldValue("seasonId", val ? val.seasonId : "")
                            }
                            getOptionLabel={(option) => option?.name && option?.year ? `${option.name} (${option.year})` : option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                                option.seasonId === value.seasonId
                            }
                            clearOnEscape
                            renderInput={(params) => (
                                <TextField {...params} fullWidth size="small" label="Season" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                            )}
                        />
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
                        <Autocomplete
                            options={["Zone", "Region", "Area", "district", "Mandal"]}
                            value={formik.values.targetLocationType || null}
                            onChange={(e, val) =>
                                formik.setFieldValue("targetLocationType", val || "")
                            }
                            getOptionLabel={(option) => option || ""}
                            clearOnEscape
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    size="small"
                                    label="Target Location Type"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                {/* Dynamic Location */}
                <Grid container spacing={3} mt={2}>
                    {visibleFields.includes("zone") && (
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete
                                options={zoneList}
                                value={
                                    zoneList.find((z) => z.zoneId === formik.values.zone) || null
                                }
                                onChange={(e, val) => {
                                    formik.setFieldValue("zone", val ? val.zoneId : "");
                                    formik.setFieldValue("region", "");
                                    formik.setFieldValue("area", "");
                                    formik.setFieldValue("district", "");
                                }}
                                getOptionLabel={(option) => option?.zoneName || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.zoneId === value.zoneId
                                }
                                clearOnEscape
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" label="Zone" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                                )}
                            />
                        </Grid>
                    )}

                    {visibleFields.includes("region") && (
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete
                                options={filteredRegions}
                                value={
                                    filteredRegions.find((r) => r.regionId === formik.values.region) ||
                                    null
                                }
                                onChange={(e, val) => {
                                    formik.setFieldValue("region", val ? val.regionId : "");
                                    formik.setFieldValue("area", "");
                                    formik.setFieldValue("district", "");
                                }}
                                getOptionLabel={(option) => option?.regionName || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.regionId === value.regionId
                                }
                                clearOnEscape
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" label="Region" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                                )}
                            />
                        </Grid>
                    )}

                    {visibleFields.includes("area") && (
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete
                                options={filteredAreas}
                                value={
                                    filteredAreas.find((a) => a.areaId === formik.values.area) || null
                                }
                                onChange={(e, val) => {
                                    formik.setFieldValue("area", val ? val.areaId : "");
                                    formik.setFieldValue("district", "");
                                }}
                                getOptionLabel={(option) => option?.areaName || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.areaId === value.areaId
                                }
                                clearOnEscape
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" label="Area" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                                )}
                            />
                        </Grid>
                    )}

                    {visibleFields.includes("district") && (
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete
                                options={filteredDistricts}
                                value={
                                    filteredDistricts.find(
                                        (d) => d.districtId === formik.values.district
                                    ) || null
                                }
                                onChange={(e, val) =>
                                    formik.setFieldValue("district", val ? val.districtId : "")
                                }
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.districtId === value.districtId
                                }
                                clearOnEscape
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" label="District" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                                )}
                            />
                        </Grid>
                    )}

                    {visibleFields.includes("mandal") && (
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <Autocomplete
                                options={filteredMandals}
                                value={
                                    filteredMandals.find(
                                        (m) => m.mandalId === formik.values.mandal
                                    ) || null
                                }
                                onChange={(e, val) =>
                                    formik.setFieldValue("mandal", val ? val.mandalId : "")
                                }
                                getOptionLabel={(option) => option?.mandalName || option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.mandalId === value.mandalId
                                }
                                clearOnEscape
                                renderInput={(params) => (
                                    <TextField {...params} fullWidth size="small" label="Mandal" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                                )}
                            />
                        </Grid>
                    )}
                </Grid>

                {/* Target Entry */}
                <Typography mt={4} mb={1} fontWeight={600}>Target Details</Typography>
                <Grid container spacing={3}>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Autocomplete
                            options={cropList}
                            value={
                                cropList.find(
                                    (c) => c.cropId === formik.values.newTarget.crop
                                ) || null
                            }
                            onChange={(e, val) => {
                                formik.setFieldValue(
                                    "newTarget.crop",
                                    val ? val.cropId : ""
                                );
                                formik.setFieldValue("newTarget.variety", "");
                            }}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                                option.cropId === value.cropId
                            }
                            clearOnEscape
                            renderInput={(params) => (
                                <TextField {...params} fullWidth size="small" label="Crop" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                            )}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <Autocomplete
                            options={filteredVarieties}
                            value={
                                filteredVarieties.find(
                                    (v) => v.varietyId === formik.values.newTarget.variety
                                ) || null
                            }
                            onChange={(e, val) =>
                                formik.setFieldValue(
                                    "newTarget.variety",
                                    val ? val.varietyId : ""
                                )
                            }
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                                option.varietyId === value.varietyId
                            }
                            clearOnEscape
                            disabled={!formik.values.newTarget.crop}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth size="small" label="Variety" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                            )}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="PY Sales Qty"
                            name="newTarget.pySalesQty"
                            value={formik.values.newTarget.pySalesQty}
                            onChange={formik.handleChange}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Sales Target Qty"
                            name="newTarget.salesTargetQty"
                            value={formik.values.newTarget.salesTargetQty}
                            onChange={formik.handleChange}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }}
                        />
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button variant="contained" disabled={!canAdd} onClick={handleAddTarget}>
                        ADD
                    </Button>
                </Box>

                {/* Target List */}
                {formik.values.targets.length > 0 && (
                    <Box mt={4}>
                        <Typography fontWeight={600} mb={1}>
                            Target Preview
                        </Typography>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f3f6f1' }}>
                                        <TableCell>Crop</TableCell>
                                        <TableCell>Variety</TableCell>
                                        <TableCell align="center">PY Sales Qty</TableCell>
                                        <TableCell align="center">Sales Target Qty</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {formik.values.targets.map((row, index) => {
                                        const cropName =
                                            cropList.find(c => c.cropId === row.crop)?.name || "-";
                                        const varietyName =
                                            varietyList.find(v => v.varietyId === row.variety)?.name || "-";

                                        return (
                                            <TableRow key={index}>
                                                <TableCell>{cropName}</TableCell>
                                                <TableCell>{varietyName}</TableCell>
                                                <TableCell align="center">{row.pySalesQty}</TableCell>
                                                <TableCell align="center">{row.salesTargetQty}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        aria-label="delete"
                                                        color="error"
                                                        onClick={() => handleDeleteTarget(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}


                {/* Totals */}
                <Grid container spacing={3} mt={5}>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth size="small" label="PY Total Sales Qty"
                            value={pyTotal} slotProps={{ input: { readOnly: true } }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField fullWidth size="small" label="Total Sales Target Qty"
                            value={targetTotal} slotProps={{ input: { readOnly: true } }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#FAFAFA' } }} />
                    </Grid>
                </Grid>

                {/* Actions */}
                <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                    <Button variant="contained" type="submit" disabled={(editId ? updateLoading : createLoading) || !formik.values.targets.length}>
                        {editId ? 'Update' : 'Submit'}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default CreateSalesTarget;
